import useRequest from "@/lib/request/useRequest";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useAuth from "@/lib/auth/useAuth";
import { CropCycleV2 } from "@/types/cropCycle";
import { CropZone } from "@/types/cropZone";
import { CropConfiguration } from "@/types/cropConfiguration";
import { SoilTexture } from "@/types/soil";
import { CoverCrop, CoverCropTermination, Tillage } from "@/types/enums";
import {
    ENROLLMENT_TYPE_OPTIONS,
    EnrollmentType,
} from "@/lib/utils/enrollment";
import {
    DEFAULT_PLANTING_DEPTH,
    DEFAULT_SEEDING_RATE,
    HarvestOperation,
    ListApplications,
    PlantingOperation,
} from "@/types/operations";
import {
    getCropCycleKey,
    getCropCycleV2Display,
    isDefaultCropCycle,
} from "@/lib/utils/cropCycles";
import { roundTo } from "@/lib/utils/numbers";
import AutoCompleteInput from "@/components/inputs/AutoCompleteInput";
import DateInput from "@/components/inputs/DateInput";
import NumberInputComponent from "@/components/inputs/NumberInput";
import SelectInput from "@/components/inputs/SelectInput";
import {
    Button,
    Chip,
    DateRangePicker,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Select,
    SelectItem,
    Spinner,
} from "@heroui/react";
import { Link } from "@tanstack/react-router";
import { DateValue, parseDate } from "@internationalized/date";
import { yupResolver } from "@hookform/resolvers/yup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faXmark } from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { boolean, date, InferType, number, object, string } from "yup";

const MIN_CROP_CYCLE_DAYS = 60;

const schema = object().shape({
    crop_configuration: number().nullable(),
    start_date: string()
        .nullable()
        .required("Start date is required")
        .test(
            "not-after-planting",
            "Crop cycle cannot start after the planting date",
            function (startDate) {
                const plantingDate = this.parent.planting_date;
                if (!startDate || !plantingDate) return true;
                return !dayjs(startDate).isAfter(dayjs(plantingDate), "day");
            },
        ),
    end_date: string()
        .nullable()
        .required("End date is required")
        .test(
            "min-duration",
            `Crop cycle must last at least ${MIN_CROP_CYCLE_DAYS} days`,
            function (endDate) {
                const startDate = this.parent.start_date;
                if (!startDate || !endDate) return true;
                return (
                    dayjs(endDate).diff(dayjs(startDate), "day") >=
                    MIN_CROP_CYCLE_DAYS
                );
            },
        ),
    planting_date: date().nullable(),
    planting_crm: number().nullable().typeError(""),
    planned_total_nitrogen: number().nullable().typeError(""),
    yield_goal: number().nullable().typeError(""),
    enrollment_type: string().required(),
    previous_crop: number().nullable(),
    tillage_practice: string().nullable(),
    soil_configuration: number().nullable(),
    soil_organic_matter: number().nullable().typeError(""),
    cover_crop: string().nullable(),
    cover_crop_termination: string().nullable(),
    has_drainage: boolean().nullable(),
    manure_use: boolean().nullable(),
    inhibitor_use: boolean().nullable(),
    eef_use: boolean().nullable(),
    n_log_validated: boolean().default(false),
    implementation_validated: boolean().default(false),
    harvest_validated: boolean().default(false),
    soil_management_validated: boolean().default(false),
});

type FormType = InferType<typeof schema>;

function ToggleAttributeButton({
    label,
    value,
    onToggle,
}: {
    label: string;
    value: boolean | null | undefined;
    onToggle: () => void;
}) {
    return (
        <Button
            size="sm"
            radius="sm"
            variant="flat"
            color={
                value === null || value === undefined
                    ? "default"
                    : value
                        ? "success"
                        : "danger"
            }
            startContent={
                value !== null && value !== undefined ? (
                    <FontAwesomeIcon icon={value ? faCheck : faXmark} />
                ) : undefined
            }
            onPress={onToggle}
        >
            {label}
        </Button>
    );
}

function ValidationToggleButton({
    label,
    value,
    onToggle,
}: {
    label: string;
    value: boolean;
    onToggle: () => void;
}) {
    return (
        <Button
            size="sm"
            radius="sm"
            variant="flat"
            color={value ? "success" : "default"}
            startContent={
                value ? <FontAwesomeIcon icon={faCheck} /> : undefined
            }
            onPress={onToggle}
        >
            {label}
        </Button>
    );
}

function DerivedMetricChip({
    label,
    value,
    isLoading,
    fieldId,
}: {
    label: string;
    value: number | null;
    isLoading: boolean;
    fieldId: string | undefined;
}) {
    return (
        <Link
            to="/dashboard/fields/$fieldId/operations"
            params={{ fieldId: fieldId ?? "" }}
            search={(prev) => prev}
            className={fieldId ? undefined : "pointer-events-none"}
        >
            <Chip
                color="primary"
                variant="flat"
                className="h-auto min-h-8 py-1 cursor-pointer"
                radius="sm"
            >
                <span className="font-medium">{label}:</span>{" "}
                {isLoading ? (
                    <Spinner size="sm" color="current" />
                ) : value != null ? (
                    roundTo(value, 1)
                ) : (
                    "—"
                )}
            </Chip>
        </Link>
    );
}

function CropCycleDetailsModal({
    isOpen,
    onClose,
}: {
    isOpen: boolean;
    onClose: () => void;
}) {
    const { useGetRequest, useMutateRequest } = useRequest();
    const { userIsSentinel } = useAuth();
    const {
        fieldId,
        fieldQuery,
        selectedCropCycle,
        setSelectedCropCycle,
        cropCycleQueryV2,
        cropZones,
        insightsParams,
    } = useFieldDashboard() ?? {};

    const cropCycles = useMemo(() => {
        return cropCycleQueryV2?.data ?? [];
    }, [cropCycleQueryV2?.data]);

    const showCropZoneInfo = (cropZones?.length ?? 0) > 1;
    const cropZoneById = useMemo(() => {
        const map = new Map<number, CropZone>();
        for (const zone of cropZones ?? []) {
            map.set(zone.id, zone);
        }
        return map;
    }, [cropZones]);

    const getCropCycleTextValue = (cropCycle: CropCycleV2) => {
        const parts = [getCropCycleV2Display(cropCycle)];
        if (isDefaultCropCycle(cropCycle)) {
            parts.push("Default");
        }
        if (showCropZoneInfo && cropCycle.crop_zone != null) {
            const zone = cropZoneById.get(cropCycle.crop_zone);
            if (zone) parts.push(zone.name);
        }
        return parts.join(" – ");
    };

    const renderCropCycleOption = (cropCycle: CropCycleV2) => {
        const zone =
            showCropZoneInfo && cropCycle.crop_zone != null
                ? cropZoneById.get(cropCycle.crop_zone)
                : undefined;
        const isDefault = isDefaultCropCycle(cropCycle);
        const secondary = [zone?.name, isDefault ? "Default" : null]
            .filter(Boolean)
            .join(" – ");
        return (
            <div className="flex min-w-0 flex-row items-center gap-2">
                {zone && (
                    <span
                        className="h-3 w-3 shrink-0 rounded-sm"
                        style={{ backgroundColor: zone.color }}
                    />
                )}
                <div className="flex min-w-0 flex-col">
                    <span className="truncate leading-tight">
                        {getCropCycleV2Display(cropCycle)}
                    </span>
                    {secondary ? (
                        <span className="truncate text-xs leading-tight text-default-400">
                            {secondary}
                        </span>
                    ) : null}
                </div>
            </div>
        );
    };

    const form = useForm<FormType>({
        resolver: yupResolver(schema),
    });

    const cropsQuery = useGetRequest<CropConfiguration[]>({
        url: "crops/",
        enabled: isOpen,
    });
    const soilsQuery = useGetRequest<SoilTexture[]>({
        url: "soils/",
        enabled: isOpen,
    });

    const [startDate, endDate, plantingDate] = form.watch([
        "start_date",
        "end_date",
        "planting_date",
    ]);
    const queryStartDate = startDate ?? selectedCropCycle?.start_date;
    const queryEndDate = endDate ?? selectedCropCycle?.end_date;

    const applicationOperationsQuery = useGetRequest<ListApplications>({
        url: `fields/${fieldId}/application_operations/`,
        enabled: isOpen && !!fieldId && !!queryStartDate,
        params: {
            start_date: queryStartDate,
            end_date: queryEndDate,
            crop_zone_id: insightsParams?.crop_zone_id,
        },
    });

    const harvestOperationsQuery = useGetRequest<HarvestOperation[]>({
        url: `fields/${fieldId}/harvest_operations/`,
        enabled: isOpen && !!fieldId && !!queryStartDate,
        params: {
            start_date: queryStartDate,
            end_date: queryEndDate,
            crop_zone_id: insightsParams?.crop_zone_id,
        },
    });

    const totalNitrogenApplied = useMemo(() => {
        const operations = applicationOperationsQuery.data?.operations;
        if (!operations?.length) return null;
        return operations
            .filter((op) => op.is_complete)
            .reduce((sum, op) => sum + (op.average_N || 0), 0);
    }, [applicationOperationsQuery.data?.operations]);

    const yieldValue = useMemo(() => {
        const harvests = harvestOperationsQuery.data;
        if (!harvests?.length) return null;
        const plantingStart = plantingDate
            ? dayjs(plantingDate).format("YYYY-MM-DD")
            : selectedCropCycle?.effective_planting_operation?.start_date;
        const harvest = plantingStart
            ? harvests
                .filter((op) => op.start_date >= plantingStart)
                .sort((a, b) => (a.start_date > b.start_date ? -1 : 1))[0]
            : harvests[0];
        return harvest?.field_averages?.yield ?? null;
    }, [
        harvestOperationsQuery.data,
        plantingDate,
        selectedCropCycle?.effective_planting_operation?.start_date,
    ]);

    const nue = useMemo(() => {
        if (yieldValue == null || totalNitrogenApplied == null) return null;
        if (totalNitrogenApplied === 0) return null;
        return totalNitrogenApplied / yieldValue;
    }, [yieldValue, totalNitrogenApplied]);

    const fieldMutation = useMutateRequest({
        url: "fields/bulk_update/",
        method: "patch",
        errorMessage: "Failed to update field details",
    });
    const plantingMutation = useMutateRequest<PlantingOperation>({
        errorMessage: "Failed to save planting operation",
    });
    const cropCycleMutation = useMutateRequest<CropCycleV2[]>({
        url: "crop_cycles/bulk_upsert/",
        method: "post",
        successMessage: "Crop cycle updated",
        errorMessage: "Failed to update crop cycle",
    });
    const validateCropCycleMutation = useMutateRequest<CropCycleV2>({
        method: "patch",
        errorMessage: "Failed to update crop cycle validation",
    });

    const planting = selectedCropCycle?.effective_planting_operation;

    useEffect(() => {
        if (!isOpen || !selectedCropCycle) return;
        form.reset({
            crop_configuration: planting?.crop_configuration?.id ?? null,
            start_date: selectedCropCycle.start_date ?? null,
            end_date: selectedCropCycle.end_date ?? null,
            planting_date: planting?.start_date
                ? dayjs(planting.start_date).toDate()
                : null,
            planting_crm: planting?.varieties?.[0]?.crm ?? null,
            planned_total_nitrogen:
                selectedCropCycle.planned_total_nitrogen ?? null,
            yield_goal: selectedCropCycle.yield_goal ?? null,
            enrollment_type:
                selectedCropCycle.enrollment_type ?? EnrollmentType.Commercial,
            previous_crop: selectedCropCycle.previous_crop ?? null,
            tillage_practice: selectedCropCycle.tillage_practice ?? null,
            soil_configuration: fieldQuery?.data?.soil_configuration ?? null,
            soil_organic_matter: fieldQuery?.data?.soil_organic_matter ?? null,
            cover_crop: selectedCropCycle.cover_crop ?? null,
            cover_crop_termination:
                selectedCropCycle.cover_crop_termination ?? null,
            has_drainage: fieldQuery?.data?.has_drainage ?? null,
            manure_use: selectedCropCycle.manure_use ?? null,
            inhibitor_use: selectedCropCycle.inhibitor_use ?? null,
            eef_use: selectedCropCycle.eef_use ?? null,
            n_log_validated: selectedCropCycle.n_log_validated ?? false,
            implementation_validated: selectedCropCycle.implementation_validated ?? false,
            harvest_validated: selectedCropCycle.harvest_validated ?? false,
            soil_management_validated: selectedCropCycle.soil_management_validated ?? false,
        });
    }, [isOpen, selectedCropCycle, planting, fieldQuery?.data, form]);

    const coverCropOptions = useMemo(
        () =>
            Object.values(CoverCrop).map((x) => ({
                id: x,
                description: x,
            })),
        [],
    );
    const terminationOptions = useMemo(
        () =>
            Object.values(CoverCropTermination).map((x) => ({
                id: x,
                description: x,
            })),
        [],
    );
    const tillageOptions = useMemo(
        () =>
            Object.values(Tillage).map((x) => ({
                id: x,
                description: x,
            })),
        [],
    );

    const [
        coverCrop,
        hasDrainage,
        manureUse,
        inhibitorUse,
        eefUse,
        nLogValidated,
        implementationValidated,
        harvestValidated,
        soilManagementValidated,
    ] = form.watch([
        "cover_crop",
        "has_drainage",
        "manure_use",
        "inhibitor_use",
        "eef_use",
        "n_log_validated",
        "implementation_validated",
        "harvest_validated",
        "soil_management_validated",
    ]);

    const dateRangeValue = useMemo(() => {
        if (!startDate) return null;
        return {
            start: parseDate(dayjs(startDate).format("YYYY-MM-DD")),
            end: endDate
                ? parseDate(dayjs(endDate).format("YYYY-MM-DD"))
                : (null as unknown as DateValue),
        };
    }, [startDate, endDate]);

    const dateRangeError =
        form.formState.errors.start_date?.message ??
        form.formState.errors.end_date?.message;

    useEffect(() => {
        if (!form.formState.isSubmitted) return;
        form.trigger(["start_date", "end_date"]);
    }, [plantingDate, form]);

    const onSubmit = async (data: FormType) => {
        if (!selectedCropCycle || !fieldId) return;

        const fieldPayload = [
            {
                id: fieldId,
                soil_configuration: data.soil_configuration,
                soil_organic_matter: data.soil_organic_matter,
                has_drainage: data.has_drainage,
            },
        ];

        const cropCyclePayload: Record<string, unknown> = {
            id: selectedCropCycle.id,
            field: fieldId,
            start_date: data.start_date,
            end_date: data.end_date,
            previous_crop: data.previous_crop,
            cover_crop: data.cover_crop,
            cover_crop_termination: data.cover_crop_termination,
            manure_use: data.manure_use,
            inhibitor_use: data.inhibitor_use,
            eef_use: data.eef_use,
            tillage_practice: data.tillage_practice,
            planned_total_nitrogen: data.planned_total_nitrogen,
            yield_goal: data.yield_goal,
            enrollment_type: data.enrollment_type,
        };

        const plantingOperationId = selectedCropCycle.planting_operation;
        const isNewPlanting =
            plantingOperationId == null || plantingOperationId === -1;
        const existingVariety = planting?.varieties?.[0];
        const plantingStartDate = data.planting_date
            ? dayjs(data.planting_date).format("YYYY-MM-DD")
            : planting?.start_date;
        const cropConfigurationId =
            data.crop_configuration ?? planting?.crop_configuration?.id;
        const preserveGdu =
            cropConfigurationId === planting?.crop_configuration?.id &&
            (data.planting_crm ?? null) === (existingVariety?.crm ?? null);

        try {
            await fieldMutation.mutateAsync({
                data: fieldPayload,
            });

            let savedPlantingId = isNewPlanting
                ? undefined
                : (plantingOperationId ?? undefined);

            if (plantingStartDate && cropConfigurationId) {
                const savedPlanting = await plantingMutation.mutateAsync({
                    url: isNewPlanting
                        ? "planting_operations/"
                        : `planting_operations/${plantingOperationId}/`,
                    method: isNewPlanting ? "post" : "put",
                    data: {
                        start_date: plantingStartDate,
                        field: Number(fieldId),
                        crop_configuration: cropConfigurationId,
                        crop_zone: selectedCropCycle.crop_zone ?? null,
                        varieties: [
                            {
                                name: existingVariety?.name,
                                grower: fieldQuery?.data?.grower,
                                crm: data.planting_crm,
                                ...(preserveGdu
                                    ? {
                                          gdd_silk: existingVariety?.gdd_silk,
                                          gdd_mature: existingVariety?.gdd_mature,
                                      }
                                    : {}),
                                maturity_level: existingVariety?.maturity_level,
                                rice_type: existingVariety?.rice_type,
                                sugarcane_type: existingVariety?.sugarcane_type,
                                soybean_maturity_group:
                                    existingVariety?.soybean_maturity_group,
                            },
                        ],
                        ...(isNewPlanting
                            ? {
                                field_rate: {
                                    rate_1:
                                        planting?.average_seeding_rate ??
                                        DEFAULT_SEEDING_RATE,
                                    rate_2:
                                        planting?.average_planting_depth ??
                                        DEFAULT_PLANTING_DEPTH,
                                },
                            }
                            : {}),
                    },
                });
                savedPlantingId = savedPlanting.id;
            }

            await cropCycleMutation.mutateAsync({
                data: [
                    {
                        ...cropCyclePayload,
                        ...(savedPlantingId != null
                            ? { planting_operation: savedPlantingId }
                            : {}),
                    },
                ],
            });

            if (userIsSentinel) {
                await validateCropCycleMutation.mutateAsync({
                    url: `crop_cycles/${selectedCropCycle.id}/validation/`,
                    data: {
                        n_log_validated: data.n_log_validated,
                        implementation_validated: data.implementation_validated,
                        harvest_validated: data.harvest_validated,
                        soil_management_validated: data.soil_management_validated,
                    },
                });
            }

            fieldQuery?.refetch?.();
            cropCycleQueryV2?.refetch?.();
            onClose();
        } catch {
            return;
        }
    };

    if (!selectedCropCycle) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            isDismissable
            size="3xl"
            placement="top"
            scrollBehavior="inside"
        >
            <ModalContent>
                <ModalHeader>
                    <div className="flex flex-col gap-1 w-full pr-8">
                        <span>Crop Cycle Details</span>
                        <Select
                            size="sm"
                            aria-label="Crop Cycle"
                            selectedKeys={
                                selectedCropCycle && cropCycles.length
                                    ? [getCropCycleKey(selectedCropCycle)]
                                    : []
                            }
                            onChange={(e) => {
                                const selected = cropCycles.find(
                                    (c) =>
                                        getCropCycleKey(c) === e.target.value,
                                );
                                if (selected) {
                                    setSelectedCropCycle?.(selected);
                                }
                            }}
                            renderValue={() => {
                                if (!selectedCropCycle) return null;
                                return renderCropCycleOption(
                                    selectedCropCycle,
                                );
                            }}
                            classNames={{
                                base: "w-full",
                            }}
                        >
                            {cropCycles.map((cropCycle) => (
                                <SelectItem
                                    key={getCropCycleKey(cropCycle)}
                                    textValue={getCropCycleTextValue(
                                        cropCycle,
                                    )}
                                >
                                    {renderCropCycleOption(cropCycle)}
                                </SelectItem>
                            ))}
                        </Select>
                    </div>
                </ModalHeader>
                <ModalBody>
                    <FormProvider {...form}>
                        <form className="flex flex-col gap-3">
                            <div className="flex flex-row gap-3 items-end">
                                <AutoCompleteInput
                                    name="crop_configuration"
                                    label="Crop"
                                    options={
                                        cropsQuery.data?.map((x) => ({
                                            id: x.id,
                                            description: x.crop_name,
                                        })) ?? []
                                    }
                                    size="sm"
                                    labelPlacement="outside"
                                    className="flex-1"
                                />

                                <DateRangePicker
                                    label="Start & End Dates"
                                    labelPlacement="outside"
                                    size="sm"
                                    showMonthAndYearPickers
                                    value={dateRangeValue}
                                    isInvalid={!!dateRangeError}
                                    errorMessage={dateRangeError}
                                    onChange={(range) => {
                                        form.setValue(
                                            "start_date",
                                            range?.start
                                                ? range.start.toString()
                                                : "",
                                            { shouldValidate: true },
                                        );
                                        form.setValue(
                                            "end_date",
                                            range?.end
                                                ? range.end.toString()
                                                : "",
                                            { shouldValidate: true },
                                        );
                                    }}
                                    className="flex-1"
                                />

                                {userIsSentinel && <SelectInput
                                    name="enrollment_type"
                                    label="Enrollment Type"
                                    options={[...ENROLLMENT_TYPE_OPTIONS]}
                                    size="sm"
                                    labelPlacement="outside"
                                    className="flex-1"
                                />}
                            </div>

                            <div className="flex flex-row gap-3 items-end">
                                <DateInput
                                    name="planting_date"
                                    label="Planting Date"
                                    size="sm"
                                    labelPlacement="outside"
                                    showMonthAndYearPickers
                                    hideErrorMessage
                                    className="flex-1"
                                />
                                <NumberInputComponent
                                    name="planting_crm"
                                    label="Planting CRM"
                                    size="sm"
                                    labelPlacement="outside"
                                    className="flex-1"
                                />
                            </div>

                            <div className="flex flex-row gap-3 items-center flex-wrap">
                                <DerivedMetricChip
                                    label="Total N Applied"
                                    value={totalNitrogenApplied}
                                    isLoading={
                                        applicationOperationsQuery.isFetching
                                    }
                                    fieldId={fieldId}
                                />
                                <DerivedMetricChip
                                    label="Yield"
                                    value={yieldValue}
                                    isLoading={
                                        harvestOperationsQuery.isFetching
                                    }
                                    fieldId={fieldId}
                                />
                                <DerivedMetricChip
                                    label="NUE"
                                    value={nue}
                                    isLoading={
                                        applicationOperationsQuery.isFetching ||
                                        harvestOperationsQuery.isFetching
                                    }
                                    fieldId={fieldId}
                                />
                            </div>

                            {userIsSentinel && (
                                <div className="flex flex-row gap-3 items-center flex-wrap">
                                    <ValidationToggleButton
                                        label="N Log Validated"
                                        value={nLogValidated}
                                        onToggle={() =>
                                            form.setValue(
                                                "n_log_validated",
                                                !nLogValidated,
                                            )
                                        }
                                    />
                                    <ValidationToggleButton
                                        label="Implementation Validated"
                                        value={implementationValidated}
                                        onToggle={() =>
                                            form.setValue(
                                                "implementation_validated",
                                                !implementationValidated,
                                            )
                                        }
                                    />
                                    <ValidationToggleButton
                                        label="Harvest Validated"
                                        value={harvestValidated}
                                        onToggle={() =>
                                            form.setValue(
                                                "harvest_validated",
                                                !harvestValidated,
                                            )
                                        }
                                    />
                                    <ValidationToggleButton
                                        label="Soil & Management Validated"
                                        value={soilManagementValidated}
                                        onToggle={() =>
                                            form.setValue(
                                                "soil_management_validated",
                                                !soilManagementValidated,
                                            )
                                        }
                                    />
                                </div>
                            )}

                            <div className="flex flex-row gap-3 items-center">
                                <NumberInputComponent
                                    name="planned_total_nitrogen"
                                    label="Planned N"
                                    size="sm"
                                    labelPlacement="outside"
                                />
                                <NumberInputComponent
                                    name="yield_goal"
                                    label="Yield Goal"
                                    size="sm"
                                    labelPlacement="outside"
                                />
                            </div>

                            <div className="flex flex-row gap-3 items-center">
                                <AutoCompleteInput
                                    name="previous_crop"
                                    label="Prev Crop"
                                    options={
                                        cropsQuery.data?.map((x) => ({
                                            id: x.id,
                                            description: x.crop_name,
                                        })) ?? []
                                    }
                                    size="sm"
                                    labelPlacement="outside"
                                />
                                <SelectInput
                                    name="tillage_practice"
                                    label="Tillage"
                                    options={tillageOptions}
                                    size="sm"
                                    labelPlacement="outside"
                                />
                            </div>

                            <div className="flex flex-row gap-3 items-center">
                                <AutoCompleteInput
                                    name="soil_configuration"
                                    label="Soil Texture"
                                    options={
                                        soilsQuery.data?.map((x) => ({
                                            id: x.id,
                                            description: x.soil_name,
                                        })) ?? []
                                    }
                                    size="sm"
                                    labelPlacement="outside"
                                />
                                <NumberInputComponent
                                    name="soil_organic_matter"
                                    label="Organic Matter"
                                    size="sm"
                                    labelPlacement="outside"
                                />
                            </div>

                            <div className="flex flex-row gap-3 items-center">
                                <SelectInput
                                    name="cover_crop"
                                    label="Cover Crop"
                                    options={coverCropOptions}
                                    size="sm"
                                    labelPlacement="outside"
                                />
                                <SelectInput
                                    name="cover_crop_termination"
                                    label="Termination"
                                    options={terminationOptions}
                                    size="sm"
                                    labelPlacement="outside"
                                    isDisabled={
                                        coverCrop === CoverCrop.None ||
                                        !coverCrop
                                    }
                                />
                            </div>

                            <div className="flex flex-row gap-3 items-center justify-evenly flex-wrap pt-1 pb-2">
                                <ToggleAttributeButton
                                    label="Drainage"
                                    value={hasDrainage}
                                    onToggle={() =>
                                        form.setValue(
                                            "has_drainage",
                                            !hasDrainage,
                                        )
                                    }
                                />
                                <ToggleAttributeButton
                                    label="Manure"
                                    value={manureUse}
                                    onToggle={() =>
                                        form.setValue("manure_use", !manureUse)
                                    }
                                />
                                <ToggleAttributeButton
                                    label="Inhibitor"
                                    value={inhibitorUse}
                                    onToggle={() =>
                                        form.setValue(
                                            "inhibitor_use",
                                            !inhibitorUse,
                                        )
                                    }
                                />
                                <ToggleAttributeButton
                                    label="EEF"
                                    value={eefUse}
                                    onToggle={() =>
                                        form.setValue("eef_use", !eefUse)
                                    }
                                />
                            </div>
                        </form>
                    </FormProvider>
                </ModalBody>
                <ModalFooter className="flex flex-row items-center gap-2">
                    <Button variant="flat" onPress={onClose} fullWidth>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        isLoading={
                            fieldMutation.isPending ||
                            plantingMutation.isPending ||
                            cropCycleMutation.isPending ||
                            validateCropCycleMutation.isPending
                        }
                        onPress={() => form.handleSubmit(onSubmit)()}
                        fullWidth
                    >
                        Save
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default CropCycleDetailsModal;
